import { AnimationLayer } from "@manager/layer/AnimationLayer";
import { ControlsLayer } from "@manager/layer/ControlsLayer";
import { CTALayer } from "@manager/layer/CTALayer";
import { PlayerContainer } from "@manager/layer/PlayerContainer";
import { PosterLayer } from "@manager/layer/PosterLayer";
import { VideoLayer } from "@manager/layer/VideoLayer";
import { PlayerContainerObserver } from "@manager/layerObserver/PlayerContainerObserver";
import React from "react";
import { I18n } from "../../i18n/i18n";
import { PlayerControls } from "../../model/playerConfiguration/PlayerControls";
import { SocialShareConfiguration } from "../../uiModel/socialShare/SocialShareConfiguration";
import { BrowserFeatureDetector } from "../../utils/BrowserFeatureDetector";
import { ListenerHelper } from "../../utils/ListenerHelper";
import { SocialShareHelper } from "../../utils/SocialShareHelper";
import { AnimationLayerComponent } from "../layer/AnimationLayerComponent";
import { ControlsLayerComponent } from "../layer/ControlsLayerComponent";
import { CTALayerComponent } from "../layer/CTALayerComponent";
import { PosterLayerComponent } from "../layer/PosterLayerComponent";
import { VideoLayerComponent } from "../layer/VideoLayerComponent";

import "@styles/c-fullscreenImitation.scss";
import "@styles/c-playerContainer.scss";
import { DebugLayerComponent, DebugLayerHandle } from "../layer/DebugLayerComponent";

interface Props {
  i18n: I18n;
  width?: number;
  height?: number;
}

interface State {
  isDebugMode: boolean;
  isMobileDevice: boolean;
  isFullscreenSupported: boolean;
  isFullscreen: boolean;
  isLargePlayer: boolean;
  socialShareConfiguration: SocialShareConfiguration;
  playIconOnPoster: boolean;
}

export class PlayerContainerComponent extends React.Component<Props, State> implements PlayerContainer {
  private static DESKTOP_LARGE_PLAYER_THRESHOLD = 1200 * 800;
  // iPhone Xr width * height
  private static MOBILE_LARGE_PLAYER_THRESHOLD = 414 * 896;
  animationLayer: AnimationLayer;
  controlsLayer: ControlsLayer;
  ctaLayer: CTALayer;
  posterLayer: PosterLayer;
  videoLayer: VideoLayer;
  playbackInformation: DebugLayerHandle;

  private observer: PlayerContainerObserver;

  private html: HTMLDivElement;

  constructor(props: Props) {
    super(props);
    this.state = {
      isDebugMode: false,
      isMobileDevice: BrowserFeatureDetector.isMobileDevice(),
      isFullscreenSupported: BrowserFeatureDetector.isFullscreenSupported(),
      isFullscreen: false,
      isLargePlayer: false,
      socialShareConfiguration: SocialShareHelper.getShareConfiguration(),
      playIconOnPoster: true,
    };
  }

  private get isReady() {
    return !!this.observer;
  }

  render() {
    const { i18n, width, height } = this.props;
    const { isDebugMode, isMobileDevice, isFullscreenSupported, isFullscreen, socialShareConfiguration, playIconOnPoster } = this.state;
    // @ts-ignore
    return (
      <div
        className={`vintom c-vintomplayerContainer 
          ${this.state.isLargePlayer ? "vintomLargePlayer" : ""} 
          ${isFullscreen && !isFullscreenSupported ? "fullscreenImitation" : ""}`}
        style={{
          width: `${width}px`,
          height: `${height}px`,
        }}
      >
        <div
          className={"c-debugContainer"}
          style={{ display: isDebugMode ? "block" : "none", flex: 1, maxWidth: "20vw", minWidth: "min-content" }}
        >
          <DebugLayerComponent className="c-debugLayer" ref={(element) => (this.playbackInformation = element)} />
        </div>

        <div
          className="vintom c-playerContainer"
          style={{ flex: 3 }}
          ref={(element) => (this.html = element)}
          onMouseMove={() => this.isReady && this.observer.onPlayerInteraction()}
          onTouchMove={() => this.isReady && this.observer.onPlayerInteraction()}
          onMouseLeave={() => this.isReady && this.observer.onPlayerInteractionEnd()}
        >
          <div
            className={`c-playerContainer__interactionLayer ${playIconOnPoster ? "play-icon-on-poster" : ""}`}
            onClick={() => this.isReady && this.observer.onPlayerClick()}
            onContextMenu={(event) => {
              this.isReady && this.observer.onPlayerContextMenu(event.clientX, event.clientY);
              event.preventDefault();
            }}
          />
          <VideoLayerComponent
            ref={(element) => (this.videoLayer = element)}
            className="c-playerContainer__layer"
            isFullscreen={this.state.isFullscreen}
          />
          <PosterLayerComponent
            ref={(element) => (this.posterLayer = element)}
            className={`c-playerContainer__layer ${isFullscreen && !isFullscreenSupported ? "fullposter" : ""}`}
          />
          <AnimationLayerComponent ref={(element) => (this.animationLayer = element)} className="c-playerContainer__layer" />
          <CTALayerComponent
            ref={(element) => (this.ctaLayer = element)}
            className="c-playerContainer__layer c-playerContainer__ctaLayer"
          />
          <ControlsLayerComponent
            ref={(element) => (this.controlsLayer = element)}
            className="c-playerContainer__layer c-playerContainer__controlsLayer"
            isMobileDevice={isMobileDevice}
            isFullscreenSupported={isFullscreenSupported}
            isFullscreen={isFullscreen}
            socialShareConfiguration={socialShareConfiguration}
            i18n={i18n}
          />
        </div>
      </div>
    );
  }

  componentDidMount() {
    ListenerHelper.addEventListener("document", "fullscreenchange", this.updateFullscreen);
    ListenerHelper.addEventListener("window", "resize", this.updateIsLargePlayer);
    this.updateIsLargePlayer();
  }

  componentWillUnmount() {
    ListenerHelper.removeEventListener("document", "fullscreenchange", this.updateFullscreen);
    ListenerHelper.removeEventListener("window", "resize", this.updateIsLargePlayer);
  }

  // start of PlayerContainer implementation
  setConfiguration(configuration: PlayerControls) {
    if (!configuration.playIconOnPoster) {
      this.setState({
        playIconOnPoster: false,
      });
    }
  }

  registerObserver(observer: PlayerContainerObserver) {
    this.observer = observer;
  }

  toggleFullscreen() {
    if (this.state.isFullscreen) {
      if (this.state.isFullscreenSupported) {
        document.exitFullscreen();
      } else {
        this.setState({
          isFullscreen: false,
        });
      }
    } else {
      if (this.state.isFullscreenSupported) {
        this.html.requestFullscreen().catch((e) => {
          console.error("If player is embedded in an iframe please make sure the iframe has allowfullscreen attribute", e);
        });
      } else {
        this.setState({
          isFullscreen: true,
        });
      }
    }
  }
  // end of PlayerContainer implementation

  toggleDebugMode = () => {
    this.setState({
      isDebugMode: !this.state.isDebugMode,
    });
  };

  private updateFullscreen = () => {
    const doc = document as any;
    const isFullscreen = !!(doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement);
    this.setState({
      isFullscreen,
    });
  };

  private updateIsLargePlayer = () => {
    if (!this.html || this.state.isMobileDevice === undefined) {
      return false;
    }
    const layerArea = this.html.clientHeight * this.html.clientWidth;
    const isLargePlayer =
      layerArea >
      (this.state.isMobileDevice
        ? PlayerContainerComponent.MOBILE_LARGE_PLAYER_THRESHOLD
        : PlayerContainerComponent.DESKTOP_LARGE_PLAYER_THRESHOLD);
    this.setState({
      isLargePlayer,
    });
  };
}
